'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  childLevel,
  headingLevel,
  items,
  SkLink,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * A numbered process ("How a consultation works", "From plan to fitted
 * kitchen"). An ordered list, so the sequence is announced as well as seen.
 * Stacks on a phone; up to four columns from 1024 px.
 */
export interface SpatialKitchenStep {
  title?: Localized;
  body?: Localized;
}

export interface SpatialKitchenStepsProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  steps?: SpatialKitchenStep[];
  ctaLabel?: Localized;
  ctaHref?: string;
  /** `dark` is the mockup's green band (`.band-dark`); the others are the shared light surfaces. */
  surface?: Surface | 'dark';
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-steps__list{display:grid;gap:2rem;counter-reset:sk-step;margin:0;padding:0;list-style:none}
@media (min-width:769px){.sk-steps__list{grid-template-columns:repeat(2,minmax(0,1fr));gap:2.5rem}}
@media (min-width:1024px){.sk-steps__list--4{grid-template-columns:repeat(4,minmax(0,1fr))}.sk-steps__list--3{grid-template-columns:repeat(3,minmax(0,1fr))}}
.sk-step{counter-increment:sk-step;display:grid;gap:.75rem;align-content:start;padding-top:1.25rem;border-top:1px solid var(--sk-border)}
.sk-step::before{content:counter(sk-step, decimal-leading-zero);font-family:var(--sk-serif);font-weight:300;font-size:1.75rem;color:var(--sk-sage-deep);line-height:1}
.sk-step__title{font-size:1.3125rem;font-weight:400}
.sk-steps__cta{margin-top:clamp(2rem,3vw,3rem)}
/* Dark green band. Literal light colours, measured on the sage #2E4A3E (WCAG 2.2):
   headings #F1F4F1 8.75:1; body #C7D3CB 6.28:1; the light brass #D9C29A 5.60:1, so it
   passes AA as normal text and carries the eyebrow and the step numbers. The
   theme's --sk-brass-text (#7A5E2F) is for light surfaces and would fail here. */
.sk-steps--dark{background:var(--sk-sage);color:#F1F4F1}
.sk-steps--dark .sk-heading{color:#F1F4F1}
.sk-steps--dark .sk-eyebrow{color:#D9C29A}
.sk-steps--dark .sk-lead,.sk-steps--dark .sk-body{color:#C7D3CB}
.sk-steps--dark .sk-step{padding-top:0;border-top:0;gap:.5rem}
.sk-steps--dark .sk-step::before{font-size:2.4rem;font-weight:400;color:#D9C29A;margin-bottom:.75rem}
.sk-steps--dark .sk-step__title{font-size:1.15rem;font-weight:500}
.sk-steps--dark a:focus-visible{outline-color:#F1F4F1}
`;

export function SpatialKitchenSteps(props: SpatialKitchenStepsProps) {
  const steps = items<SpatialKitchenStep>(props.steps).slice(0, 6);
  const title = text(props.title, 'How it works');
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const StepTitle = childLevel(level);
  const ctaLabel = text(props.ctaLabel);
  const titleId = useId();
  const columns = steps.length % 4 === 0 ? 4 : 3;
  const dark = props.surface === 'dark';

  return (
    <BlockRoot
      as="section"
      className={`sk sk-steps sk-section ${dark ? 'sk-steps--dark' : surfaceClass(props.surface, 'white')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <SkStyles id="steps" css={CSS} />
      <div className="sk-container">
        <div className="sk-section-head">
          {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
          <Title id={titleId} className="sk-heading sk-h2">
            {title}
          </Title>
          {text(props.intro) && <p className="sk-lead">{text(props.intro)}</p>}
        </div>
        <ol className={`sk-steps__list sk-steps__list--${columns}`}>
          {steps.map((step, index) => (
            <li key={`${text(step.title)}-${index}`} className="sk-step">
              <StepTitle className="sk-heading sk-step__title">{text(step.title)}</StepTitle>
              {text(step.body) && <p className="sk-body">{text(step.body)}</p>}
            </li>
          ))}
        </ol>
        {ctaLabel && props.ctaHref && (
          <p className="sk-steps__cta">
            <SkLink href={props.ctaHref} className={`sk-btn ${dark ? 'sk-btn--light' : 'sk-btn--primary'}`}>
              {ctaLabel}
              <Icon name="arrow-right" size={16} />
            </SkLink>
          </p>
        )}
      </div>
    </BlockRoot>
  );
}
